<template>
  <component
    :class="{ 'compact-selection-mode': showCompactSelection }"
    :chips="resolvedChips"
    :clearable="type !== 'vTextField'"
    :deletable-chips="deletableChips"
    :dense="dense"
    :disabled="disabled"
    :hint="hint"
    @input="selected"
    :is="type"
    @keydown.enter.prevent="enterPress"
    :label="label"
    :items="items"
    :item-text="itemText"
    :item-value="itemValue"
    :menu-props="menuProps"
    :multiple="multiple"
    :no-data-text="noDataText"
    :persistent-hint="persistentHint"
    ref="field"
    :return-object="returnObject"
    :rules="rules"
    :small-chips="resolvedSmallChips"
    :value="value"
    v-bind="{...inputProps}"
  >
    <template v-if="useCustomSelection" v-slot:selection="{ item, index }">
      <v-chip
        v-if="hasSelectionText(item) && shouldRenderSelectionChip(index)"
        class="compact-selection-chip"
        :small="smallChips"
        :close="deletableChips"
        @click:close="removeCompactSelectionIndex(index, $event)"
      >
        {{ getSelectionText(item) }}
      </v-chip>
      <span v-else-if="hasSelectionText(item) && showCompactSelection && index === 0">
        {{ getSelectionText(item) }}
      </span>
      <span v-else-if="showCompactSelection && index === effectiveVisibleCount && compactSelectionCount > 0" class="caption grey--text text--darken-1">+{{ compactSelectionCount }}</span>
      <span v-else-if="hasSelectionText(item) && !chips && index === 0">
        {{ getSelectionText(item) }}
      </span>
      <span v-else-if="hasSelectionText(item) && !chips">
        , {{ getSelectionText(item) }}
      </span>
    </template>
<!--    add margin-right to X in text-field so it will line up with X in autocomplete, combobox, and select -->
    <template v-slot:append v-if="type === 'vTextField' && value">
      <span style="margin-right: 28px"><v-icon @click="$emit('input', '')">mdi-close</v-icon></span>
    </template>
  </component>
</template>

<script>
  import { VAutocomplete, VCombobox, VSelect, VTextField } from "vuetify/lib"

  export default {
    name: "SearchInput",
    components: { VAutocomplete, VCombobox, VSelect, VTextField },
    props: {
      chips: { type: Boolean, default: false },
      deletableChips: { type: Boolean, default: false },
      dense: { type: Boolean, default: false },
      disabled: { type: Boolean, default: false },
      hint: { type: String },
      inputProps: { type: Object, default: () => ({})},
      items: { type: Array, default: () => ([]) },
      itemText: {},
      itemValue: {},
      selectionText: {},
      keepMenuOpenOnSelect: { type: Boolean, default: false },
      label: String,
      menuProps: { type: Object },
      multiple: { type: Boolean, default: false },
      noDataText: { type: String },
      persistentHint: { type: Boolean, default: false },
      compactSelection: { type: Boolean, default: false },
      compactSelectionFirstAsChip: { type: Boolean, default: false },
      compactSelectionVisibleCount: { type: Number, default: 1 },
      returnObject: { type: Boolean, default: false },
      rules: { default: () => [] },
      smallChips: { type: Boolean, default: false },
      type: { type: String, default: 'vTextField'},
      value: { required: true }
    },
    computed: {
      showCompactSelection() {
        return this.compactSelection && this.multiple && Array.isArray(this.value) && this.value.length > 0;
      },

      useCustomSelection() {
        return this.showCompactSelection || this.selectionText !== undefined;
      },

      effectiveVisibleCount() {
        if (!this.showCompactSelection) return 0;
        return Math.max(1, this.compactSelectionVisibleCount);
      },

      compactSelectionCount() {
        return Math.max((this.value || []).length - this.effectiveVisibleCount, 0);
      },

      resolvedChips() {
        return this.showCompactSelection ? false : this.chips;
      },

      resolvedSmallChips() {
        return this.showCompactSelection ? false : this.smallChips;
      }
    },
    methods: {
      enterPress() {
        setTimeout(()=>{ this.$refs.field.isMenuActive = false }, 10);// close dropdown when enter pressed
        this.$emit('enter')
      },

      selected(value) {
        if (!(this.multiple && this.keepMenuOpenOnSelect)) {
          setTimeout(()=>{ this.$refs.field.isMenuActive = false }, 10);// close dropdown when selected
        }
        this.$emit('input', value);
      },

      getItemText(item) {
        if (typeof this.itemText === 'function') return this.itemText(item);
        if (typeof this.itemText === 'string' && item && typeof item === 'object') return item[this.itemText];
        if (item && typeof item === 'object') return item.text || item.name || item.value || '';
        return item;
      },

      getItemValue(item) {
        if (typeof this.itemValue === 'function') return this.itemValue(item);
        if (typeof this.itemValue === 'string' && item && typeof item === 'object') return item[this.itemValue];
        if (item && typeof item === 'object' && Object.prototype.hasOwnProperty.call(item, 'value')) return item.value;
        return item;
      },

      getSelectionText(item) {
        if (typeof this.selectionText === 'function') return this.selectionText(item);
        if (typeof this.selectionText === 'string' && item && typeof item === 'object') {
          const value = item[this.selectionText];
          if (value !== undefined && value !== null && value !== '') return value;
        }
        return this.getItemText(item);
      },

      hasSelectionText(item) {
        const value = this.getSelectionText(item);
        return value !== undefined && value !== null && value !== '';
      },

      shouldRenderSelectionChip(index) {
        if (this.showCompactSelection) {
          return this.compactSelectionFirstAsChip && index < this.effectiveVisibleCount;
        }
        return !!this.chips;
      },

      removeCompactSelectionIndex(index, e) {
        if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
        if (!Array.isArray(this.value)) return;
        const next = [...this.value];
        next.splice(index, 1);
        this.$emit('input', next);
      }

    }
  }
</script>

<style scoped>
  .compact-selection-chip {
    margin: 0 !important;
    height: 19px !important;
    max-height: 19px !important;
    line-height: 19px !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }

  .compact-selection-chip ::v-deep .v-chip__content {
    line-height: 19px !important;
  }

  .compact-selection-mode ::v-deep .v-select__selections {
    min-height: 19px !important;
    max-height: 19px !important;
    align-items: center !important;
    flex-wrap: nowrap !important;
    overflow: hidden !important;
  }

  .compact-selection-mode ::v-deep .v-select__selection {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }

  .compact-selection-mode ::v-deep .v-select__selections input {
    flex: 0 0 1px !important;
    width: 1px !important;
    min-width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    margin: 0 !important;
  }

</style>
